/**
 * # MessageContent — Memoized Markdown Chat Bubble
 *
 * Renders a single Scout chat message as a themed bubble with full markdown support.
 * Designed for streaming AI responses where tokens arrive incrementally.
 *
 * ## Architecture: 3-Layer Memoization
 *
 * This follows the Vercel AI SDK "block-level memoization" pattern
 * (see: ai-sdk.dev/cookbook/next/markdown-chatbot-with-memoization).
 *
 * The problem: when an assistant streams tokens, the message content string changes
 * on every chunk. A naive `<ReactMarkdown>{content}</ReactMarkdown>` would re-parse
 * and re-render the entire markdown tree on every token — O(n) rendering per token,
 * O(n^2) total for a full response.
 *
 * The solution is three layers of memoization:
 *
 * 1. **Message level** (`MessageContent`, `React.memo` with custom comparator):
 *    Completed messages (status: "complete") have stable props — they never re-render
 *    while a different message is streaming. This means only 1 out of N messages in
 *    the conversation re-renders per token.
 *
 * 2. **Block level** (`MemoizedMarkdownBlock`, `React.memo` on each block):
 *    Within the actively streaming message, `marked.lexer()` splits the markdown into
 *    block-level tokens (paragraphs, headings, code fences, etc.). Completed blocks
 *    produce identical raw strings across renders, so `React.memo` skips their
 *    re-render. Only the last (in-progress) block actually re-renders per token.
 *
 * 3. **Component map stability** (`markdownComponents` at module scope):
 *    The `components` prop passed to `react-markdown` is a plain object defined once
 *    at module level — not inside a render function. This prevents `react-markdown`
 *    from seeing a new `components` reference on every render, which would defeat
 *    its own internal memoization.
 *
 * ## Why `marked` + `react-markdown` together?
 *
 * `marked` is used **only** for its lexer (`marked.lexer()`) to split raw markdown
 * into discrete block-level tokens. It does NOT render HTML.
 *
 * `react-markdown` handles all rendering — it parses each block into an AST via
 * remark/rehype and renders it through the MUI component map. This gives us full
 * control over how each markdown element looks (Typography, Box, Link, etc.) while
 * staying in the React tree (no `dangerouslySetInnerHTML`).
 */
import { Box, Link, Typography, keyframes } from "@mui/material";
import { marked } from "marked";
import { memo, useMemo } from "react";
import Markdown, { type Components } from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

import { ScoutChatMessagePart, ScoutTextPart } from "./scoutChatMessage";

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

const compactListSx = {
  mt: 0.375,
  mb: 1.125,
  pl: 2.75,
  lineHeight: 1.5,
  "& > li": {
    my: 0.125,
  },
  "& ul, & ol": {
    my: 0.125,
    pl: 2.25,
  },
  "& p": {
    my: 0,
  },
} as const;

/**
 * Maps markdown element types to MUI components for consistent theming.
 *
 * Defined at **module scope** (not inside a component) so the object reference is
 * stable across renders. If this were created inside a render function,
 * `react-markdown` would receive a new `components` prop every render and bypass
 * its internal memoization — re-rendering the entire markdown tree even when the
 * content string hasn't changed.
 */
const markdownComponents: Components = {
  p: ({ children }) => (
    <Typography variant="body1" sx={{ mt: 0.375, mb: 1.4, lineHeight: 1.5 }}>
      {children}
    </Typography>
  ),
  a: ({ href, children }) => (
    <Link
      href={href ?? undefined}
      target="_blank"
      rel="noopener noreferrer"
      color="inherit"
      underline="always"
    >
      {children}
    </Link>
  ),
  pre: ({ children }) => (
    <Box
      component="pre"
      sx={{
        bgcolor: "grey.900",
        color: "grey.100",
        p: 1.5,
        borderRadius: 1,
        overflowX: "auto",
        mt: 1.5,
        mb: 2,
        fontSize: "0.9375rem",
        lineHeight: 1.6,
      }}
    >
      {children}
    </Box>
  ),
  code: ({ children, className }) => {
    if (className) {
      return <code className={className}>{children}</code>;
    }

    return (
      <Box
        component="code"
        sx={{
          bgcolor: "action.hover",
          px: 0.5,
          py: 0.25,
          borderRadius: 0.5,
          fontFamily: "monospace",
          fontSize: "0.85em",
        }}
      >
        {children}
      </Box>
    );
  },
  ul: ({ children }) => (
    <Box component="ul" sx={compactListSx}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={compactListSx}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Box component="li" sx={{ whiteSpace: "normal" }}>
      {children}
    </Box>
  ),
  h1: ({ children }) => (
    <Typography component="h1" variant="h6" sx={{ mt: 2.75, mb: 0.9 }}>
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography component="h2" variant="h6" sx={{ mt: 2.75, mb: 0.9 }}>
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography component="h3" variant="subtitle1" fontWeight={600} sx={{ mt: 2.5, mb: 0.65 }}>
      {children}
    </Typography>
  ),
  h4: ({ children }) => (
    <Typography component="h4" variant="subtitle2" fontWeight={600} sx={{ mt: 2.25, mb: 0.65 }}>
      {children}
    </Typography>
  ),
  blockquote: ({ children }) => (
    <Box
      component="blockquote"
      sx={{
        borderLeft: 3,
        borderColor: "primary.main",
        pl: 1.25,
        py: 0.25,
        mt: 1.125,
        mb: 1.75,
        ml: 0,
        opacity: 0.85,
        "& > :first-of-type": {
          mt: 0,
        },
        "& > :last-child": {
          mb: 0,
        },
      }}
    >
      {children}
    </Box>
  ),
  hr: () => (
    <Box
      component="hr"
      sx={{ border: "none", borderTop: 1, borderColor: "divider", mt: 1.75, mb: 2.25 }}
    />
  ),
  table: ({ children }) => (
    <Box sx={{ overflowX: "auto", mt: 1.5, mb: 2 }}>
      <Box
        component="table"
        sx={{ borderCollapse: "collapse", width: "100%", fontSize: "0.9375rem" }}
      >
        {children}
      </Box>
    </Box>
  ),
  th: ({ children }) => (
    <Box
      component="th"
      sx={{ border: 1, borderColor: "divider", px: 1, py: 0.5, fontWeight: 600, textAlign: "left" }}
    >
      {children}
    </Box>
  ),
  td: ({ children }) => (
    <Box component="td" sx={{ border: 1, borderColor: "divider", px: 1, py: 0.5 }}>
      {children}
    </Box>
  ),
};

const markdownRemarkPlugins = [remarkGfm, remarkBreaks];

/**
 * Uses `marked.lexer()` to split a markdown string into block-level tokens
 * (paragraphs, headings, code fences, lists, etc.) and returns their raw strings.
 *
 * This is the key to block-level memoization: when a new streaming token arrives,
 * the full content is re-tokenized, but completed blocks produce the exact same
 * raw string as the previous render. `MemoizedMarkdownBlock` uses a string equality
 * check to skip re-rendering those stable blocks.
 *
 * Note: the lexer itself is O(n) per call (it runs on the full string every time).
 * The win is in avoiding O(n) *rendering* for stable blocks — only the last
 * (actively streaming) block's React tree is rebuilt.
 */
function parseMarkdownIntoBlocks(markdown: string): string[] {
  const tokens = marked.lexer(markdown);
  return tokens.map((token) => token.raw);
}

/**
 * Renders a single markdown block via `react-markdown` with MUI component overrides.
 *
 * Wrapped in `React.memo` with a custom comparator that checks string equality on
 * `content`. This is Layer 2 of the memoization strategy: when the parent
 * `MemoizedMarkdown` re-renders (because the streaming message got a new token),
 * blocks whose raw string hasn't changed are skipped entirely.
 *
 * In practice during streaming, all blocks except the last one are stable — so this
 * memo saves us from re-parsing and re-rendering the entire conversation history
 * worth of markdown on every token.
 */
const MemoizedMarkdownBlock = memo(
  function MemoizedMarkdownBlock({ content }: { content: string }) {
    return (
      <Markdown remarkPlugins={markdownRemarkPlugins} components={markdownComponents}>
        {content}
      </Markdown>
    );
  },
  (prev, next) => prev.content === next.content
);
MemoizedMarkdownBlock.displayName = "MemoizedMarkdownBlock";

/**
 * Splits markdown content into block-level tokens and renders each as a memoized
 * block. This is the orchestrator between `parseMarkdownIntoBlocks` (the splitter)
 * and `MemoizedMarkdownBlock` (the per-block renderer).
 *
 * Keys are `${messageId}-block_${index}` — stable for a given message as long as
 * blocks aren't inserted above (which doesn't happen during streaming since tokens
 * append to the end).
 *
 * The `useMemo` on `parseMarkdownIntoBlocks` ensures the lexer only runs when the
 * content string actually changes, not on unrelated parent re-renders.
 */
const MemoizedMarkdown = memo(function MemoizedMarkdown({
  content,
  blockKey,
}: {
  content: string;
  blockKey: string;
}) {
  const blocks = useMemo(() => parseMarkdownIntoBlocks(content), [content]);

  return (
    <>
      {blocks.map((block, index) => (
        <MemoizedMarkdownBlock key={`${blockKey}-${index}`} content={block} />
      ))}
    </>
  );
});
MemoizedMarkdown.displayName = "MemoizedMarkdown";

function isThinkingPart(part: ScoutTextPart): boolean {
  return part.agentState === "thinking";
}

const TextMessagePart = memo(
  function TextMessagePart({ part }: { part: ScoutTextPart }) {
    if (isThinkingPart(part)) {
      return (
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            my: 0,
            lineHeight: 1.4,
            whiteSpace: "pre-line",
            transform: "translateY(3px)",
          }}
        >
          {part.text}
        </Typography>
      );
    }

    return <MemoizedMarkdown content={part.text} blockKey={part.id} />;
  },
  (prev, next) =>
    prev.part.id === next.part.id &&
    prev.part.text === next.part.text &&
    prev.part.agentState === next.part.agentState
);
TextMessagePart.displayName = "TextMessagePart";

export const MessageContent = memo(function MessageContent({
  messageId,
  parts,
  isStreaming,
}: {
  messageId: string;
  parts: ScoutChatMessagePart[];
  isStreaming?: boolean;
}) {
  const visibleTextParts = useMemo(
    () => parts.filter((part): part is ScoutTextPart => part.type === "text"),
    [parts]
  );
  const hasRenderableText = visibleTextParts.some((part) => part.text.length > 0);

  if (hasRenderableText) {
    return (
      <Box
        component="div"
        sx={{
          "& > :first-of-type": {
            mt: 0,
          },
          "& > p:first-of-type": {
            transform: "translateY(2px)",
          },
          "& > h1:first-of-type, & > h2:first-of-type, & > h3:first-of-type, & > h4:first-of-type":
            {
              transform: "translateY(-2px)",
            },
          "& > :last-child": {
            mb: 0,
          },
        }}
      >
        {visibleTextParts.map((part) => (
          <TextMessagePart key={`${messageId}-${part.id}`} part={part} />
        ))}
      </Box>
    );
  }

  if (isStreaming) {
    return (
      <Typography variant="body1">
        <Box
          component="span"
          sx={{
            display: "inline-block",
            width: "2px",
            height: "1em",
            bgcolor: "text.primary",
            ml: 0.25,
            verticalAlign: "text-bottom",
            animation: `${blink} 1s step-end infinite`,
          }}
        />
      </Typography>
    );
  }

  return null;
});
MessageContent.displayName = "MessageContent";
